import { useTranslation } from 'react-i18next';

import { ApiError, NetworkError } from '../lib/apiError';

interface ErrorNoticeProps {
  error: unknown;
}

/**
 * Anzeige eines Fehlers.
 *
 * Der Text kommt aus `errors.<CODE>` in den Sprachdateien -- **nicht** aus
 * `detail` der Serverantwort. docs/05, Abschnitt 4: Das Frontend wertet
 * `code` aus, niemals `title` oder `detail`. Der Servertext dient nur als
 * Rueckfall fuer Codes, die diese Oberflaeche noch nicht kennt; so bleibt ein
 * neu eingefuehrter Fehlercode lesbar, statt als leerer Kasten zu erscheinen.
 *
 * Die Vorgangskennung wird mit angezeigt. Sie steht zugleich im
 * Serverprotokoll -- damit ist ein Anwenderbericht ohne Rueckfragen
 * auffindbar.
 */
export function ErrorNotice({ error }: ErrorNoticeProps) {
  const { t, i18n } = useTranslation();

  if (error instanceof NetworkError) {
    return (
      <div className="notice notice--danger" role="alert">
        <span className="notice__title">{t('errors.title')}</span>
        <span>{t('errors.network')}</span>
      </div>
    );
  }

  if (error instanceof ApiError) {
    const key = `errors.${error.code}`;
    const known = i18n.exists(key);

    return (
      <div className="notice notice--danger" role="alert">
        <span className="notice__title">{t('errors.title')}</span>
        <span>{known ? t(key) : error.detail || t('errors.unexpected')}</span>

        {error.fieldErrors.length > 0 ? (
          <ul>
            {error.fieldErrors.map((item) => (
              <li key={`${item.field}-${item.code}`}>
                {item.field}: {item.message}
              </li>
            ))}
          </ul>
        ) : null}

        {error.requestId ? (
          <span className="notice__meta">
            {t('errors.requestId')}: {error.requestId}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="notice notice--danger" role="alert">
      <span className="notice__title">{t('errors.title')}</span>
      <span>{t('errors.unexpected')}</span>
    </div>
  );
}
